import { api } from "../scripts/api";

/*
Who to name on a class card.

Says WHICH it is showing rather than collapsing both into one line. class_table
.created_by only records who clicked New class, so a class David set up for Eric
to teach would otherwise read as David's, and on an admin dashboard listing every
class on the platform that is exactly the distinction being looked for.

Nothing is shown when neither is known, rather than a placeholder: an empty line
is honest, "Unknown" is noise.
*/
// MODULE scope, not inside ownerLine. It was declared as a const inside that
// function and then used from buildOwnerFilter() and paint() as well, which threw
// ReferenceError: esc is not defined on every load and surfaced only as
// "Couldn't load your classes" (2026-08-19).
const esc = (t) => String(t ?? "").replace(/[<>&]/g, "");

// The single answer to "whose class is this", so the card and the filter can
// never disagree about it.
function ownerName(c) {
   return c.professor_teaching || c.created_by_name || "";
}

function ownerLine(c) {
   if (c.professor_teaching) {
      return `<div class="class-owner">${esc(c.professor_teaching)}</div>`;
   }
   if (c.created_by_name) {
      return `<div class="class-owner muted">created by ${esc(c.created_by_name)}</div>`;
   }
   return "";
}

class AppClasses extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="classes">
            <header class="classes-header">
               <h1>My classes</h1>
            </header>

            <div class="classes-actions">
               <label class="owner-filter">Professor
                  <select id="owner-filter"><option value="">All</option></select>
               </label>
               <button class="btn-primary" id="btn-new-class">+ New class</button>
            </div>

            <div class="classes-grid" id="classes-grid"></div>
         </section>

         <dialog class="class-dialog" id="new-class-dialog">
            <form method="dialog" id="new-class-form">
               <h2>New class</h2>
               <label>Course code
                  <input name="course_code" placeholder="FIN 413" required maxlength="20" />
               </label>
               <label>Section
                  <input name="section" placeholder="001" required maxlength="10" />
               </label>
               <label>Title
                  <input name="title" placeholder="Investments" maxlength="100" />
               </label>
               <label>Term
                  <input name="term" placeholder="Fall 2026" required maxlength="30" />
               </label>
               <div class="dialog-actions">
                  <p class="msg err" id="new-class-error"></p>
                  <div class="dialog-buttons">
                     <button type="submit" class="btn-primary" id="btn-add-class">Add</button>
                     <button type="button" class="btn-secondary" id="btn-cancel-class">Cancel</button>
                  </div>
               </div>
            </form>
         </dialog>
      `;

      // Button: create a new class
      this.dialog = this.querySelector("#new-class-dialog");
      this.form = this.querySelector("#new-class-form");
      this.querySelector("#btn-new-class").addEventListener("click", () => this.openDialog());
      this.querySelector("#btn-cancel-class").addEventListener("click", () => this.dialog.close());
      this.form.addEventListener("submit", (e) => this.onSubmit(e));

      // Cards: event delegation on the grid, so it survives re-renders
      this.querySelector("#classes-grid").addEventListener("click", (e) => {
         const card = e.target.closest(".class-card");
         if (!card) return;
         const classId = card.dataset.classId;
         if (classId) this.openClass(classId);
      });
   }

   show() {
      if (this.loaded) return;
      this.loaded = true;
      this.render();
   }

   async render() {
      const grid = this.querySelector("#classes-grid");
      grid.innerHTML = `<p class="empty">Loading...</p>`;
      try {
         const res = await api("/list-classes");
         const me = await res.json();
         if (!res.ok || !me.ok) throw new Error("failed");

         this.classes = me.classes || [];
         // /list-classes now returns the caller's role; default to the
         // least-privileged reading so a stale server cannot grant a Delete
         // button that the API would refuse anyway.
         this.role = me.role || "professor";
         if (!this.classes.length) {
            grid.innerHTML = `<p class="empty">No classes yet. Create one to get started.</p>`;
            return;
         }
         this.buildOwnerFilter();
         this.paint();
      } catch (err) {
         // Bind and LOG it. A bare `catch {}` here turned a one-line ReferenceError
         // into a generic failure message with nothing in the console, which cost
         // a round trip to diagnose. The message stays friendly; the cause is now
         // findable.
         console.error("classes render failed:", err);
         grid.innerHTML = `<p class="msg err">Couldn't load your classes.</p>`;
      }
   }

   /*
   Populate the professor filter from the classes actually loaded.

   Filtering happens in the browser rather than as a server round trip: /list-classes
   already returns every class the viewer may see, so the whole set is in hand and a
   query parameter would only add a way for the two to disagree.

   An ADMIN sees every class on the platform, which is why this exists: David asked
   to be able to set the dev and demo classes aside, and they are distinguished by
   whose they are. A professor with one or two classes gets a filter listing one or
   two names, which is harmless.
   */
   buildOwnerFilter() {
      const sel = this.querySelector("#owner-filter");
      if (!sel) return;
      const names = [...new Set(this.classes.map(ownerName).filter(Boolean))].sort();
      const keep = sel.value;
      sel.innerHTML = `<option value="">All professors</option>`
         + names.map((n) => `<option value="${esc(n)}">${esc(n)}</option>`).join("");
      // Survive a re-render: if the previously chosen professor still has classes,
      // stay on them rather than silently snapping back to All.
      if (keep && names.includes(keep)) sel.value = keep;
      if (!sel.dataset.wired) {
         sel.dataset.wired = "1";
         sel.addEventListener("change", () => this.paint());
      }
   }

   /*
   Card buttons.

   Delegated from the grid and stopPropagation'd, because the whole card is a
   clickable button that opens the class: without that, archiving would also
   navigate away from the list you are trying to tidy.
   */
   wireCardActions() {
      const grid = this.querySelector("#classes-grid");
      if (!grid || grid.dataset.wired) return;
      grid.dataset.wired = "1";
      grid.addEventListener("click", async (e) => {
         const btn = e.target.closest(".card-act");
         if (!btn) return;
         e.preventDefault();
         e.stopPropagation();
         const id = Number(btn.dataset.id);
         const cls = this.classes.find((c) => Number(c.class_id) === id);
         if (!cls) return;
         if (btn.dataset.act === "archive") await this.toggleArchive(cls, btn);
         else await this.deleteClass(cls, btn);
      });
   }

   async toggleArchive(cls, btn) {
      const next = !cls.active;
      btn.disabled = true;
      try {
         const r = await api(`/classes/${cls.class_id}/active`, {
            method: "PATCH", body: JSON.stringify({ active: next }),
         });
         if (!r.ok) throw new Error(`archive ${r.status}`);
         await this.render();
      } catch (err) {
         btn.disabled = false;
         alert("Could not change that class. Please try again.");
      }
   }

   /*
   Delete, with the server's refusal surfaced rather than swallowed.

   The first call is unforced, so a class holding real student work comes back 409
   with the counts. Those counts go into the confirm prompt, which is the whole
   point: the decision to destroy a term of grades should be made while looking at
   how many there are, not before.
   */
   async deleteClass(cls, btn) {
      const name = `${cls.course_code} · ${cls.section}`;
      if (!confirm(`Delete ${name} permanently?\n\nArchive instead if you just want it `
                 + `out of the way - archiving keeps all grades and can be undone.`)) return;
      btn.disabled = true;
      try {
         let r = await api(`/classes/${cls.class_id}`, { method: "DELETE" });
         if (r.status === 409) {
            const body = await r.json().catch(() => ({}));
            if (body.reason === "still_referenced") {
               alert(body.detail || "Something still references this class.");
               btn.disabled = false;
               return;
            }
            if (!confirm(`${body.detail}\n\nDelete anyway? This cannot be undone.`)) {
               btn.disabled = false;
               return;
            }
            r = await api(`/classes/${cls.class_id}?force=1`, { method: "DELETE" });
         }
         if (r.status === 403) {
            const body = await r.json().catch(() => ({}));
            alert(body.detail || "Only an admin can delete a class.");
            btn.disabled = false;
            return;
         }
         if (!r.ok) throw new Error(`delete ${r.status}`);
         await this.render();
      } catch (err) {
         btn.disabled = false;
         alert("Could not delete that class. Nothing was changed.");
      }
   }

   paint() {
      const grid = this.querySelector("#classes-grid");
      const want = (this.querySelector("#owner-filter") || {}).value || "";
      const rows = want ? this.classes.filter((c) => ownerName(c) === want) : this.classes;
      grid.innerHTML = rows.length
         ? rows.map((c, i) => this.renderCard(c, i)).join("")
         : `<p class="empty">No classes for ${esc(want)}.</p>`;
      this.wireCardActions();
   }

   renderCard(c,i) {
      const archived = !c.active;
      return `
         <div class="class-card${archived ? " archived" : ""}" data-class-id="${c.class_id}" role="button" tabindex="0" style="--i:${i}">
            <div class="class-code">${c.course_code} · ${c.section}</div>
            <div class="class-title">${c.title || ""}</div>
            <div class="class-term">${c.term}</div>
            ${ownerLine(c)}
            <div class="class-actions">
               <button type="button" class="card-act" data-act="archive"
                  data-id="${c.class_id}">${archived ? "Restore" : "Archive"}</button>
               ${this.role === "admin"
                  ? `<button type="button" class="card-act danger" data-act="delete"
                        data-id="${c.class_id}">Delete</button>` : ""}
            </div>
            <div class="class-stats">
               <div class="stat">
                  <div class="stat-num">${c.students}</div>
                  <div class="stat-label">students</div>
               </div>
               <div class="stat">
                  <div class="stat-num">${c.assignments}</div>
                  <div class="stat-label">assignments</div>
               </div>
               <span class="status-pill ${archived ? "archived" : "active"}">
                  ${archived ? "Archived" : "Active"}
               </span>
            </div>
         </div>
      `;
   }

   openDialog() {
      this.form.reset();
      this.querySelector("#new-class-error").textContent = "";
      this.dialog.showModal();
   }

   async onSubmit(e) {
      e.preventDefault();     // stop the dialog closing until the request succeeds
      const btn = this.querySelector("#btn-add-class");
      const errEl = this.querySelector("#new-class-error");
      errEl.textContent = "";

      const data = Object.fromEntries(new FormData(this.form));
      const payload = {
         course_code: data.course_code.trim(),
         section: data.section.trim(),
         title: (data.title || "").trim(),
         term: data.term.trim(),
      };

      btn.disabled = true;
      btn.textContent = "Adding...";
      try {
         const res = await api("/create-classes", {
            method: "POST",
            body: JSON.stringify(payload),
         });
         const result = await res.json();
         if (!res.ok || !result.ok) throw new Error(result.reason || "failed");

         this.dialog.close();
         this.loaded = false;
         this.show();
      } catch {
         errEl.textContent = "Couldn't add the class. Check the fields and try again.";
      } finally {
         btn.disabled = false;
         btn.textContent = "Add";
      }
   }

   openClass(classId) {
      location.hash = `class-detail/${encodeURIComponent(classId)}`;
   }
}

customElements.define("app-classes", AppClasses);